/**
 * Module summary:
 * This file handles trusted-device checks, server-backed session bootstrap, and
 * passkey/WebAuthn flows. It translates browser credential data into the
 * request shapes expected by the backend auth API.
 */

import { getFirebaseAuth } from '../firebaseConfig';

const AUTH_API_BASE = process.env.REACT_APP_AUTH_API_BASE || '/api';
const DEVICE_ID_KEY = 'macha.deviceId';

type JsonRecord = Record<string, unknown>;
type ApiErrorPayload = { error?: string };
type GrecaptchaLike = { execute: (siteKey: string, input: { action: string }) => Promise<string> };

export interface TrustedDeviceRecord {
  deviceHash: string;
  label?: string;
  lastSeenIP?: string;
  lastSeenUA?: string;
  createdAt?: unknown;
  expiresAt?: unknown;
}

export interface WebAuthnCredentialRecord {
  credIdHash: string;
  label?: string;
  createdAt?: unknown;
  lastUsedAt?: unknown;
  revoked?: boolean;
}

export function isWebAuthnSupported(): boolean {
  // Check for the browser WebAuthn entry point before showing passkey actions.
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';
}

export async function isPlatformAuthenticatorSupported(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    // Ask the browser whether this device supports a built-in authenticator such
    // as Face ID, Touch ID, or Windows Hello.
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    // Append one byte at a time so the base64-url string preserves the exact challenge payload.
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    // Step through each decoded character to rebuild the original byte array.
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function ensureArrayBuffer(value: string): ArrayBuffer {
  return fromBase64Url(value).buffer;
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  return toBase64Url(new Uint8Array(buffer));
}

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Always include cookies because the backend session endpoints rely on
  // HTTP-only session state in addition to JSON request bodies.
  const response = await fetch(`${AUTH_API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  let payload: unknown = {};
  try {
    // Parse JSON when available so callers can work with typed payloads.
    payload = await response.json();
  } catch {
    // Default to an empty object because not every error response guarantees JSON.
    payload = {};
  }

  if (!response.ok) {
    const errorPayload = payload as ApiErrorPayload;
    throw new Error(errorPayload.error || `API request failed (${response.status})`);
  }

  return payload as T;
}

function mapWebAuthnError(err: unknown, fallback: string): Error {
  if (!(err instanceof Error)) return new Error(fallback);
  if (err.name === 'NotAllowedError') {
    return new Error('Passkey request was cancelled or timed out. Please try again.');
  }
  if (err.name === 'InvalidStateError') {
    return new Error('This device still has an existing passkey for this account. Remove the old passkey from device settings, then try again.');
  }
  if (err.name === 'AbortError') {
    return new Error('Passkey request was interrupted. Please try again.');
  }
  return err;
}

export function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  // Persist one stable device id so the backend can recognize repeat browsers.
  const generated = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, generated);
  return generated;
}

async function maybeAttachRecaptcha(action: string): Promise<string> {
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const maybeGrecaptcha = (window as { grecaptcha?: GrecaptchaLike }).grecaptcha;
  if (!siteKey || !maybeGrecaptcha?.execute) return '';
  try {
    // Execute reCAPTCHA only when configured so suspicious flows can send a bot-risk token to the backend.
    return await maybeGrecaptcha.execute(siteKey, { action });
  } catch {
    return '';
  }
}

export async function preAuthCheck(emailOrUid: string): Promise<{
  trusted: boolean;
  requireRecaptcha: boolean;
  requireMfa: boolean;
}> {
  // Ask the backend whether this login attempt looks trusted before deciding
  // how much friction the UI should add.
  return apiRequest('/preAuthCheck', {
    method: 'POST',
    body: JSON.stringify({
      emailOrUid,
      deviceId: getOrCreateDeviceId(),
    }),
  });
}

export async function sessionLogin(input: {
  idToken: string;
  rememberDevice: boolean;
  label?: string;
}): Promise<JsonRecord> {
  const recaptchaToken = await maybeAttachRecaptcha('session_login');
  return apiRequest('/sessionLogin', {
    method: 'POST',
    body: JSON.stringify({
      idToken: input.idToken,
      rememberDevice: input.rememberDevice,
      label: input.label || 'Browser device',
      deviceId: getOrCreateDeviceId(),
      recaptchaToken,
    }),
  });
}

async function withAuthHeader(init: RequestInit = {}): Promise<RequestInit> {
  const currentUser = getFirebaseAuth().currentUser;
  if (!currentUser) {
    throw new Error('No authenticated user');
  }
  const idToken = await currentUser.getIdToken();
  return {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: `Bearer ${idToken}`,
    },
  };
}

export async function registerWebAuthnCredential(label?: string): Promise<void> {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported on this browser');
  }

  const options = await apiRequest<PublicKeyCredentialCreationOptions>('/webauthn/registerOptions', await withAuthHeader({
    method: 'POST',
    body: JSON.stringify({}),
  }));

  const publicKey: PublicKeyCredentialCreationOptions = {
    ...options,
    challenge: ensureArrayBuffer(options.challenge as unknown as string),
    user: {
      ...(options.user as PublicKeyCredentialUserEntity),
      id: ensureArrayBuffer((options.user as any).id),
    },
    excludeCredentials: Array.isArray(options.excludeCredentials)
      ? options.excludeCredentials.map((cred: any) => ({
          ...cred,
          id: ensureArrayBuffer(cred.id),
        }))
      : [],
  };

  let credential: PublicKeyCredential | null = null;
  try {
    credential = await navigator.credentials.create({ publicKey }) as PublicKeyCredential;
  } catch (err) {
    throw mapWebAuthnError(err, 'Passkey registration failed.');
  }
  if (!credential) {
    throw new Error('Credential registration was cancelled');
  }

  const attestation = credential.response as AuthenticatorAttestationResponse;
  const registrationResponse = {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(attestation.clientDataJSON),
      attestationObject: bufferToBase64Url(attestation.attestationObject),
      transports: typeof attestation.getTransports === 'function' ? attestation.getTransports() : [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };

  await apiRequest('/webauthn/register', await withAuthHeader({
    method: 'POST',
    body: JSON.stringify({ response: registrationResponse, label }),
  }));
}

export async function lookupWebAuthnCredentials(emailOrUid: string): Promise<boolean> {
  if (!isWebAuthnSupported() || !emailOrUid.trim()) return false;
  const result = await apiRequest<{ hasCredentials: boolean }>('/webauthn/lookup', {
    method: 'POST',
    body: JSON.stringify({ emailOrUid }),
  });
  return !!result.hasCredentials;
}

export async function loginWithWebAuthn(emailOrUid?: string, rememberDevice = true): Promise<{ customToken: string }> {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn is not supported on this browser');
  }

  const authOptionsPayload = await apiRequest<{ options: PublicKeyCredentialRequestOptions; challengeId: string }>('/webauthn/authOptions', {
    method: 'POST',
    body: JSON.stringify({ emailOrUid }),
  });

  const options = authOptionsPayload.options;
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...options,
    challenge: ensureArrayBuffer(options.challenge as unknown as string),
    allowCredentials: Array.isArray(options.allowCredentials)
      ? options.allowCredentials.map((cred: any) => ({
          ...cred,
          id: ensureArrayBuffer(cred.id),
        }))
      : [],
  };

  let credential: PublicKeyCredential | null = null;
  try {
    credential = await navigator.credentials.get({ publicKey }) as PublicKeyCredential;
  } catch (err) {
    throw mapWebAuthnError(err, 'Passkey sign-in failed.');
  }
  if (!credential) {
    throw new Error('Credential authentication was cancelled');
  }

  const assertion = credential.response as AuthenticatorAssertionResponse;
  const responsePayload = {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64Url(assertion.clientDataJSON),
      authenticatorData: bufferToBase64Url(assertion.authenticatorData),
      signature: bufferToBase64Url(assertion.signature),
      userHandle: assertion.userHandle ? bufferToBase64Url(assertion.userHandle) : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };

  const recaptchaToken = await maybeAttachRecaptcha('webauthn_auth');

  return apiRequest('/webauthn/auth', {
    method: 'POST',
    body: JSON.stringify({
      challengeId: authOptionsPayload.challengeId,
      response: responsePayload,
      rememberDevice,
      deviceId: getOrCreateDeviceId(),
      recaptchaToken,
      label: 'WebAuthn trusted device',
    }),
  });
}

export async function listTrustedDevices(): Promise<TrustedDeviceRecord[]> {
  const authInit = await withAuthHeader({ method: 'GET' });
  const response = await fetch(`${AUTH_API_BASE}/account/trustedDevices`, {
    credentials: 'include',
    ...authInit,
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error || 'Failed to list trusted devices');
  return (payload.devices || []) as TrustedDeviceRecord[];
}

export async function revokeTrustedDevice(deviceHash: string): Promise<void> {
  await apiRequest('/account/trustedDevices/revoke', await withAuthHeader({
    method: 'POST',
    body: JSON.stringify({ deviceHash }),
  }));
}

export async function listWebauthnCredentials(): Promise<WebAuthnCredentialRecord[]> {
  const authInit = await withAuthHeader({ method: 'GET' });
  const response = await fetch(`${AUTH_API_BASE}/account/webauthnCredentials`, {
    credentials: 'include',
    ...authInit,
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.error || 'Failed to list WebAuthn credentials');
  return (payload.credentials || []) as WebAuthnCredentialRecord[];
}

export async function revokeWebauthnCredential(credIdHash: string): Promise<void> {
  await apiRequest('/account/webauthnCredentials/revoke', await withAuthHeader({
    method: 'POST',
    body: JSON.stringify({ credIdHash }),
  }));
}
